<script setup lang="ts">
import {
  FAQ_CATEGORIES,
  buildLevelFaq,
  hexAlpha,
  mixColor,
  type LevelTier,
} from '@/utils/levels';

// O `tier` fica só pra pintura (chips, pílula de busca) — as respostas são
// textos oficiais estáticos, sem nada derivado do jogador.
defineProps<{
  tier: LevelTier;
}>();

const activeCategory = ref<string>(FAQ_CATEGORIES[0].key);
const openQuestion = ref<string | null>(null);

const allItems = computed(() => buildLevelFaq());

const items = computed(() =>
  allItems.value.filter((item) => item.category === activeCategory.value),
);

/* ---------------- busca (mesmo padrão do TabsFAQ da home) ---------------- */

const searchOpen = ref(false);
const searchQuery = ref('');
const searchInputRef = ref<HTMLInputElement | null>(null);
const searchPillRef = ref<HTMLElement | null>(null);

// A pílula cresce/encolhe via Web Animations API — `transition` está morta no
// app inteiro (reset global do main.scss), e aqui a largura-alvo é fixa.
const PILL_CLOSED = '34px';
const PILL_OPEN = '220px';

function toggleSearch() {
  const isOpening = !searchOpen.value;

  searchOpen.value = isOpening;

  if (isOpening) {
    openQuestion.value = null;
    nextTick(() => {
      searchInputRef.value?.focus();
      searchPillRef.value?.animate(
        [{ width: PILL_CLOSED }, { width: PILL_OPEN }],
        { duration: 350, easing: 'ease', fill: 'forwards' },
      );
    });

    return;
  }

  closeSearchPill();
}

function closeSearchPill() {
  searchQuery.value = '';
  openQuestion.value = null;

  const pill = searchPillRef.value;

  if (!pill) return;

  pill.getAnimations().forEach((animation) => animation.cancel());
  pill.animate([{ width: PILL_OPEN }, { width: PILL_CLOSED }], {
    duration: 300,
    easing: 'ease',
    fill: 'forwards',
  });
}

/**
 * Busca em TODAS as categorias, pergunta e resposta — o resultado mostra a
 * categoria em cima da pergunta, como na home.
 */
const searchResults = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();

  if (!query) return [];

  return allItems.value
    .filter(
      (item) =>
        item.question.toLowerCase().includes(query) ||
        item.answer.toLowerCase().includes(query),
    )
    .map((item) => ({
      ...item,
      categoryLabel:
        FAQ_CATEGORIES.find((category) => category.key === item.category)
          ?.label ?? '',
    }));
});

/** A lista renderizada: resultado da busca quando ela está aberta. */
const visibleItems = computed(() =>
  searchOpen.value ? searchResults.value : items.value,
);

function selectCategory(key: string) {
  if (searchOpen.value) {
    searchOpen.value = false;
    closeSearchPill();
  }

  activeCategory.value = key;
  // Trocar de categoria fecha o que estava aberto — a pergunta aberta não
  // pertence mais à lista visível.
  openQuestion.value = null;
}

function toggle(question: string) {
  openQuestion.value = openQuestion.value === question ? null : question;
}

/**
 * URLs nas respostas viram links clicáveis, como no FAQ da home. As respostas
 * são texto NOSSO (tabela estática), então o v-html é seguro — ainda assim o
 * texto é escapado antes de injetar as âncoras, por higiene.
 */
function linkify(answer: string) {
  const escaped = answer
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  return escaped.replace(
    /https?:\/\/[^\s<]+/g,
    (url) => `<a href="${url}">${url}</a>`,
  );
}
</script>

<template>
  <section class="flex flex-col gap-4">
    <div class="flex flex-col gap-1.5">
      <h2
        class="text-[22px] font-extrabold leading-[27px] tracking-[-0.04em] text-white"
      >
        Dúvidas frequentes
      </h2>
      <p class="max-w-[560px] text-[13px] leading-[18px] text-white/[0.62]">
        As perguntas que mais aparecem sobre XP, elos e cashback.
      </p>
    </div>

    <!-- Categorias + busca -->
    <div class="flex flex-wrap items-center gap-2">
      <!-- Pílula de busca, no padrão do FAQ da home: fechada é só a lupa;
           clicou, cresce e vira input. -->
      <button
        ref="searchPillRef"
        :aria-expanded="searchOpen"
        aria-label="Buscar nas dúvidas"
        class="levels-faq-search flex h-[34px] flex-none items-center justify-center gap-1.5 overflow-hidden rounded-full px-2.5"
        :style="
          searchOpen
            ? {
                background: hexAlpha(tier.color, 0.14),
                boxShadow: `inset 0 0 0 1px ${hexAlpha(tier.color, 0.45)}`,
              }
            : {
                background: 'rgba(255,255,255,.045)',
                boxShadow: 'inset 0 0 0 1px rgba(255,255,255,.07)',
              }
        "
        type="button"
        @click.stop="!searchOpen && toggleSearch()"
      >
        <NuxtIcon
          class="flex-none text-[13px] text-white/70"
          :filled="true"
          name="search"
          @click.stop="toggleSearch"
        />
        <input
          v-if="searchOpen"
          ref="searchInputRef"
          v-model="searchQuery"
          class="min-w-0 flex-1 border-none bg-transparent text-[12px] font-medium text-white caret-white outline-none placeholder:text-white/40"
          placeholder="Buscar nas dúvidas..."
          type="text"
          @click.stop
        />
        <NuxtIcon
          v-if="searchOpen"
          class="flex-none cursor-pointer text-[11px] text-white/50"
          :filled="true"
          name="close"
          @click.stop="toggleSearch"
        />
      </button>

      <button
        v-for="category in FAQ_CATEGORIES"
        :key="category.key"
        class="whitespace-nowrap rounded-full px-4 py-2 text-[12px] font-semibold leading-4"
        :style="
          activeCategory === category.key && !searchOpen
            ? {
                background: hexAlpha(tier.color, 0.18),
                color: '#fff',
                boxShadow: `inset 0 0 0 1px ${hexAlpha(tier.color, 0.5)}`,
              }
            : {
                background: 'rgba(255,255,255,.045)',
                color: 'rgba(255,255,255,.62)',
                boxShadow: 'inset 0 0 0 1px rgba(255,255,255,.07)',
              }
        "
        type="button"
        @click="selectCategory(category.key)"
      >
        {{ category.label }}
      </button>
    </div>

    <!-- Perguntas (ou resultados da busca, quando ela está aberta) -->
    <div
      class="overflow-hidden rounded-2xl bg-white/[0.02] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06)]"
    >
      <p
        v-if="searchOpen && searchQuery.trim() === ''"
        class="px-5 py-4 text-[13px] leading-[18px] text-white/50"
      >
        Digite algo para buscar nas dúvidas
      </p>
      <p
        v-else-if="searchOpen && searchResults.length === 0"
        class="px-5 py-4 text-[13px] leading-[18px] text-white/50"
      >
        Nenhum resultado para "{{ searchQuery }}"
      </p>

      <div
        v-for="(item, index) in visibleItems"
        :key="item.question"
        :style="{
          boxShadow:
            index === 0 ? 'none' : 'inset 0 1px 0 rgba(255,255,255,.06)',
        }"
      >
        <button
          class="flex w-full items-center gap-4 px-5 py-4 text-left"
          type="button"
          @click="toggle(item.question)"
        >
          <span class="flex min-w-0 flex-1 flex-col gap-0.5">
            <!-- No resultado de busca a categoria aparece em cima, como na home -->
            <span
              v-if="searchOpen && 'categoryLabel' in item"
              class="text-[10px] font-medium uppercase leading-[13px] tracking-[0.04em] text-white/[0.35]"
            >
              {{ item.categoryLabel }}
            </span>
            <span
              class="text-[14px] font-semibold leading-[19px]"
              :style="{
                color:
                  openQuestion === item.question
                    ? '#fff'
                    : 'rgba(255,255,255,.82)',
              }"
            >
              {{ item.question }}
            </span>
          </span>
          <span
            class="flex-none text-[13px] leading-none"
            :style="{
              color: mixColor(tier.color, '#ffffff', 0.5),
              transform:
                openQuestion === item.question ? 'rotate(180deg)' : 'none',
            }"
          >
            ▾
          </span>
        </button>

        <p
          v-if="openQuestion === item.question"
          class="levels-answer px-5 pb-4 text-[13px] font-normal leading-[20px] text-white/[0.72]"
          v-html="linkify(item.answer)"
        ></p>
      </div>
    </div>
  </section>
</template>

<style scoped lang="postcss">
/*
  A largura da pílula de busca fica AQUI, não em utility: o Tailwind roda com
  `important: true`, e `width !important` vence a animação WAAPI que expande a
  pílula (declaração important ganha de animation na cascata) — com a utility
  o clique não abria nada. CSS scoped sem important deixa o animate() mandar.
*/
.levels-faq-search {
  width: 34px;
  min-width: 34px;
}

/* `transition` é zerada globalmente no main.scss; a abertura entra por keyframe. */
@keyframes levelsAnswerIn {
  0% {
    opacity: 0;
    transform: translateY(-4px);
  }
  100% {
    opacity: 1;
    transform: none;
  }
}

.levels-answer {
  animation: levelsAnswerIn 0.28s cubic-bezier(0.2, 0.75, 0.3, 1) both;
}

/* Links dentro das respostas — mesmo azul do FAQ da home. */
.levels-answer :deep(a) {
  color: #00c9ff;
  text-decoration: underline;
}
</style>
